<template>
  <div class="admin-room-container figma-property-page">
    <div class="figma-white-card">
      <div class="figma-page-header">
        <div class="figma-page-title">{{ $t('slideshow.pageTitle') }}</div>
        <div class="figma-header-divider"></div>
        <div class="figma-header-row">
          <div class="figma-btn-group">
            <el-button size="small" class="figma-btn-add" @click="_addRow">
              <i class="el-icon-plus"></i>{{ $t('slideshow.action.add') }}
            </el-button>
            <el-button size="small" class="figma-btn-refresh" title="刷新" @click="_queryMethod">
              <i class="el-icon-refresh"></i>
            </el-button>
          </div>
          <div class="figma-search-group">
            <el-input size="small" v-model="conditions.title"
              :placeholder="$t('slideshow.search.keywordPlaceholder')" class="figma-search-input" clearable
              @keyup.enter.native="_queryMethod" />
            <el-button size="small" class="figma-btn-search" @click="_queryMethod">
              <i class="el-icon-search"></i>{{ $t('common.search') }}
            </el-button>
          </div>
        </div>
      </div>

      <div class="figma-table-section">
        <el-table ref="slideshowTable" :data="tableData" style="width: 100%" :fit="true" row-key="slideId" :header-cell-style="headerCellStyle" :cell-style="cellStyle">
          <el-table-column :label="$t('slideshow.table.sort')" width="76" align="center">
            <template slot-scope="scope">
              <span class="drag-handle" draggable="true" :title="$t('slideshow.audit.dragTip')" @dragstart="_handleDragStart($event, scope.$index)" @dragend="_handleDragEnd">
                <i class="el-icon-rank"></i>
              </span>
            </template>
          </el-table-column>
          <el-table-column prop="slideId" :label="$t('slideshow.table.id')" min-width="80" align="center">
            <template slot-scope="scope">{{ scope.row.slideId || '-' }}</template>
          </el-table-column>
          <el-table-column prop="title" :label="$t('slideshow.table.name')" min-width="140" align="center">
            <template slot-scope="scope">{{ scope.row.title || '-' }}</template>
          </el-table-column>
          <el-table-column prop="imageUrl" :label="$t('slideshow.table.image')" min-width="124" align="center">
            <template slot-scope="scope">
              <el-image v-if="getImageUrl(scope.row)" :src="getImageUrl(scope.row)" :preview-src-list="[getImageUrl(scope.row)]" style="width: 60px; height: 40px; border-radius: 4px;" fit="cover" />
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="imageUrl" :label="$t('slideshow.table.imageUrl')" min-width="175" align="center">
            <template slot-scope="scope">
              <span v-if="getImageUrl(scope.row)" class="figma-link-text">{{ getImageUrl(scope.row) }}</span>
              <span v-else>-</span>
            </template>
          </el-table-column>
          <el-table-column prop="stateName" :label="$t('slideshow.table.state')" min-width="132" align="center">
            <template slot-scope="scope">
              <span :class="['status-tag', getStateClass(scope.row.state || scope.row.statusCd)]">{{ getStateName(scope.row) }}</span>
            </template>
          </el-table-column>
          <el-table-column prop="remark" :label="$t('slideshow.table.remark')" min-width="195" align="center">
            <template slot-scope="scope">{{ scope.row.remark || '-' }}</template>
          </el-table-column>
          <el-table-column prop="createTime" :label="$t('slideshow.table.createTime')" min-width="217" align="center">
            <template slot-scope="scope">{{ scope.row.createTime || '-' }}</template>
          </el-table-column>
          <el-table-column prop="updateTime" :label="$t('slideshow.table.updateTime')" min-width="232" align="center">
            <template slot-scope="scope">{{ scope.row.updateTime || '-' }}</template>
          </el-table-column>
          <el-table-column :label="$t('slideshow.table.operation')" fixed="right" min-width="160" align="center">
            <template slot-scope="scope">
              <span class="action-links">
                <a class="action-link" @click="_editRow(scope.row)">{{ $t('slideshow.action.edit') }}</a>
                <a class="action-link action-link-danger" @click="_deleteRow(scope.row)">{{ $t('slideshow.action.delete') }}</a>
              </span>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <div class="figma-pagination">
        <span class="pagination-total">共{{ pagination.total }}条</span>
        <el-pagination :current-page="pagination.current" :page-sizes="[10, 20, 30, 50]"
          :page-size="pagination.size" layout="prev, pager, next, jumper" :total="pagination.total"
          @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        <el-select v-model="pagination.size" size="small" class="pagination-size-select">
          <el-option label="10条/页" :value="10" /><el-option label="20条/页" :value="20" /><el-option label="50条/页" :value="50" />
        </el-select>
      </div>

      <el-dialog
        :title="dialogTitle"
        :visible.sync="dialogVisible"
        width="650px"
        custom-class="figma-slideshow-dialog"
        :close-on-click-modal="false"
        :show-close="false"
        @close="handleDialogClose"
      >
        <el-form ref="slideshowFormRef" :model="formData" :rules="formRules" label-width="72px" size="small" class="slideshow-dialog-form">
          <el-form-item :label="$t('slideshow.form.name') + ':'" prop="title" class="slideshow-name-form-item">
            <el-input v-model="formData.title" :placeholder="$t('slideshow.form.namePlaceholder')" />
          </el-form-item>
          <el-form-item :label="$t('slideshow.form.image') + ':'" prop="imageUrl" class="slideshow-image-form-item">
            <el-upload
              class="slideshow-uploader"
              action="#"
              accept="image/*"
              :auto-upload="false"
              :show-file-list="false"
              :on-change="handleImageChange"
            >
              <div v-if="imagePreviewUrl" class="slideshow-preview">
                <el-image :src="imagePreviewUrl" :preview-src-list="[imagePreviewUrl]" fit="cover" />
                <div class="slideshow-preview-mask">
                  <i class="el-icon-upload2"></i>
                  <span>{{ $t('slideshow.upload.reselect') }}</span>
                </div>
              </div>
              <div v-else class="slideshow-upload-placeholder">
                <i class="el-icon-plus"></i>
              </div>
            </el-upload>
          </el-form-item>
          <el-form-item :label="$t('slideshow.form.state') + ':'" prop="state" class="slideshow-status-form-item">
            <el-switch v-model="formData.state" active-value="1" inactive-value="2" active-color="#0E76FF" inactive-color="#DCE6F5" />
            <span class="slideshow-status-text">{{ formData.state === '1' ? $t('slideshow.form.show') : $t('slideshow.form.hide') }}</span>
          </el-form-item>
          <el-form-item :label="$t('slideshow.form.link') + ':'" class="slideshow-link-form-item">
            <div class="slideshow-link-row">
              <el-input v-model="formData.linkUrl" :placeholder="$t('slideshow.form.linkPlaceholder')" />
              <el-button size="small" class="slideshow-select-link-btn" @click="chooseSlideshowLink">{{ $t('slideshow.action.selectLink') }}</el-button>
            </div>
          </el-form-item>
          <el-form-item :label="$t('slideshow.form.weight') + ':'" class="slideshow-weight-form-item">
            <el-input v-model="formData.weight" :placeholder="$t('slideshow.form.weightPlaceholder')" />
          </el-form-item>
          <el-form-item :label="$t('slideshow.form.remark') + ':'" class="slideshow-remark-form-item">
            <el-input v-model="formData.remark" :placeholder="$t('slideshow.form.remarkPlaceholder')" />
          </el-form-item>
        </el-form>
        <div slot="footer" class="dialog-footer slideshow-dialog-footer">
          <el-button size="small" class="slideshow-btn-cancel" @click="dialogVisible = false">{{ $t('slideshow.audit.cancel') }}</el-button>
          <el-button size="small" class="slideshow-btn-confirm" :loading="submitLoading || uploadLoading" @click="submitSlideshow">{{ $t('slideshow.action.confirm') }}</el-button>
        </div>
      </el-dialog>
    </div>
  </div>
</template>

<script>
import { listSlideshow, saveSlideshow, updateSlideshow, deleteSlideshow, uploadSlideshowImage } from '@/api/platform/platformApi'

export default {
  name: 'SlideshowList',
  data() {
    return {
      conditions: { title: '' },
      tableData: [],
      pagination: { current: 1, size: 10, total: 0 },
      dialogVisible: false,
      dialogTitle: '',
      isEdit: false,
      uploadLoading: false,
      submitLoading: false,
      sortSaving: false,
      draggingIndex: null,
      dragOverIndex: null,
      dragBody: null,
      dragOverHandler: null,
      dropHandler: null,
      imagePreviewUrl: '',
      localPreviewUrl: '',
      formData: this.getDefaultFormData(),
      formRules: {
        title: [{ required: true, message: this.$t('slideshow.validate.nameRequired'), trigger: 'blur' }],
        imageUrl: [{ required: true, message: this.$t('slideshow.validate.imageRequired'), trigger: 'change' }],
        state: [{ required: true, message: this.$t('slideshow.validate.stateRequired'), trigger: 'change' }]
      }
    }
  },
  created() {
    this._listData(this.pagination.current, this.pagination.size)
  },
  mounted() {
    this.$nextTick(this.bindTableDragEvents)
  },
  beforeDestroy() {
    this.unbindTableDragEvents()
    this.revokeLocalPreviewUrl()
  },
  methods: {
    getDefaultFormData() {
      return {
        slideId: '',
        title: '',
        imageUrl: '',
        state: '1',
        linkUrl: '',
        seq: '',
        remark: ''
      }
    },
    _listData(page, size) {
      listSlideshow({ ...this.conditions, page, row: size }).then(res => {
        const records = res.data || res.slideshows || []
        this.tableData = Array.isArray(records) ? records : []
        this.pagination.total = Number(res.total || res.records || res.count || this.tableData.length || 0)
        this.$nextTick(this.bindTableDragEvents)
      }).catch(error => {
        this.$message.error(error.message || this.$t('slideshow.audit.fetchError'))
      })
    },
    bindTableDragEvents() {
      const table = this.$refs.slideshowTable && this.$refs.slideshowTable.$el
      const body = table && table.querySelector('.el-table__body-wrapper tbody')
      if (!body || body === this.dragBody) return
      this.unbindTableDragEvents()
      this.dragBody = body
      this.dragOverHandler = event => {
        if (this.draggingIndex === null || this.sortSaving) return
        const rowEl = event.target.closest('tr')
        if (!rowEl || !rowEl.parentNode) return
        event.preventDefault()
        this.dragOverIndex = Array.prototype.indexOf.call(rowEl.parentNode.children, rowEl)
        this.updateDragRowClass()
        if (event.dataTransfer) event.dataTransfer.dropEffect = 'move'
      }
      this.dropHandler = event => {
        if (this.draggingIndex === null || this.sortSaving) return
        const rowEl = event.target.closest('tr')
        if (!rowEl || !rowEl.parentNode) return
        event.preventDefault()
        const targetIndex = Array.prototype.indexOf.call(rowEl.parentNode.children, rowEl)
        this.handleSortDrop(this.draggingIndex, targetIndex)
      }
      body.addEventListener('dragover', this.dragOverHandler)
      body.addEventListener('drop', this.dropHandler)
    },
    unbindTableDragEvents() {
      if (this.dragBody) {
        if (this.dragOverHandler) this.dragBody.removeEventListener('dragover', this.dragOverHandler)
        if (this.dropHandler) this.dragBody.removeEventListener('drop', this.dropHandler)
      }
      this.dragBody = null
      this.dragOverHandler = null
      this.dropHandler = null
    },
    _handleDragStart(event, index) {
      if (this.sortSaving) return
      this.draggingIndex = index
      this.dragOverIndex = index
      if (event.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('text/plain', String(index))
      }
      this.updateDragRowClass()
    },
    _handleDragEnd() {
      this.draggingIndex = null
      this.dragOverIndex = null
      this.updateDragRowClass()
    },
    updateDragRowClass() {
      this.$nextTick(() => {
        const table = this.$refs.slideshowTable && this.$refs.slideshowTable.$el
        const rows = table ? table.querySelectorAll('.el-table__body-wrapper tbody tr') : []
        rows.forEach((row, index) => {
          row.classList.toggle('is-dragging-row', index === this.draggingIndex)
          row.classList.toggle('is-drag-over-row', index === this.dragOverIndex && index !== this.draggingIndex)
        })
      })
    },
    getSortValue(row, index) {
      const value = row.seq !== undefined && row.seq !== null && row.seq !== '' ? row.seq : (row.sort !== undefined && row.sort !== null && row.sort !== '' ? row.sort : row.weight)
      return value !== undefined && value !== null && value !== '' ? value : (this.pagination.current - 1) * this.pagination.size + index + 1
    },
    async handleSortDrop(fromIndex, targetIndex) {
      const oldList = this.tableData.slice()
      if (fromIndex === targetIndex || fromIndex < 0 || targetIndex < 0 || fromIndex >= oldList.length || targetIndex >= oldList.length) {
        this._handleDragEnd()
        return
      }
      const newList = oldList.slice()
      const moved = newList.splice(fromIndex, 1)[0]
      newList.splice(targetIndex, 0, moved)
      // 按新顺序重新分配 seq（步长 10，留间隔方便后续插入）
      this.tableData = newList.map((item, index) => ({ ...item, seq: (index + 1) * 10 }))
      this._handleDragEnd()
      await this.saveSortOrder(oldList)
    },
    async saveSortOrder(oldList) {
      this.sortSaving = true
      try {
        await Promise.all(this.tableData.map(row => {
          const params = { ...row, slideId: this.getRowId(row), imageUrl: this.getImageUrl(row) }
          return updateSlideshow(params)
        }))
        this.$message.success(this.$t('slideshow.audit.sortSuccess'))
        this._listData(this.pagination.current, this.pagination.size)
      } catch (error) {
        this.tableData = oldList
        this.$message.error(error.message || this.$t('slideshow.audit.sortFailed'))
      } finally {
        this.sortSaving = false
      }
    },
    _queryMethod() {
      this.pagination.current = 1
      this._listData(this.pagination.current, this.pagination.size)
    },
    _addRow() {
      this.isEdit = false
      this.dialogTitle = this.$t('slideshow.dialog.addTitle')
      this.dialogVisible = true
      this.$nextTick(() => {
        this.resetDialogForm()
        this.formData.seq = this.getNextSeq()
      })
    },
    getNextSeq() {
      if (!this.tableData || this.tableData.length === 0) return 10
      const maxSeq = Math.max(...this.tableData.map(row => {
        const v = Number(row.seq || row.sort || row.weight || 0)
        return isNaN(v) ? 0 : v
      }))
      return maxSeq + 10
    },
    _editRow(row) {
      this.isEdit = true
      this.dialogTitle = this.$t('slideshow.dialog.editTitle')
      this.dialogVisible = true
      this.$nextTick(() => {
        this.resetDialogForm()
        this.formData = {
          slideId: this.getRowId(row),
          title: row.title || '',
          imageUrl: this.getImageUrl(row),
          state: row.state !== undefined && row.state !== null && row.state !== '' ? String(row.state) : String(row.statusCd || '1'),
          linkUrl: row.linkUrl || row.link || row.jumpUrl || '',
          seq: row.seq || row.sort || row.weight || '',
          remark: row.remark || ''
        }
        this.imagePreviewUrl = this.formData.imageUrl
        this.$refs.slideshowFormRef && this.$refs.slideshowFormRef.clearValidate()
      })
    },
    chooseSlideshowLink() {
      this.$message.info(this.$t('slideshow.audit.linkSelectTodo'))
    },
    _deleteRow(row) {
      this.$confirm(this.$t('slideshow.audit.deleteConfirm'), this.$t('slideshow.audit.deleteTitle'), {
        confirmButtonText: this.$t('slideshow.action.delete'),
        cancelButtonText: this.$t('slideshow.audit.cancel'),
        type: 'warning'
      }).then(() => {
        deleteSlideshow({ slideId: this.getRowId(row) }).then(() => {
          this.$message.success(this.$t('slideshow.audit.deleteSuccess'))
          this._queryMethod()
        }).catch(error => {
          this.$message.error(error.message || this.$t('slideshow.audit.deleteFailed'))
        })
      }).catch(() => {})
    },
    getRowId(row) {
      return row.slideId || row.id || ''
    },
    getImageUrl(row) {
      return row.imageUrl || row.url || row.fileUrl || row.photoUrl || ''
    },
    getStateName(row) {
      if (row.stateName) return row.stateName
      const state = String(row.state !== undefined && row.state !== null ? row.state : row.statusCd)
      return state === '2' ? this.$t('slideshow.table.stateHide') : this.$t('slideshow.table.stateShow')
    },
    getStateClass(state) {
      return { '0': 'status-normal', '1': 'status-normal', '2': 'status-danger', '3': 'status-warning' }[String(state)] || 'status-normal'
    },
    handleImageChange(file) {
      const rawFile = file.raw || file
      if (!this.validateImageFile(rawFile)) {
        return false
      }
      this.revokeLocalPreviewUrl()
      this.localPreviewUrl = URL.createObjectURL(rawFile)
      this.imagePreviewUrl = this.localPreviewUrl
      this.uploadImage(rawFile)
      return true
    },
    validateImageFile(file) {
      const isImageType = file.type && file.type.indexOf('image/') === 0
      const isImageName = /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(file.name || '')
      if (!isImageType && !isImageName) {
        this.$message.error(this.$t('slideshow.upload.onlyImage'))
        return false
      }
      return true
    },
    async uploadImage(file) {
      this.uploadLoading = true
      try {
        const formData = new FormData()
        formData.append('uploadFile', file)
        formData.append('communityId', '-1')
        const res = await uploadSlideshowImage(formData)
        const imageUrl = this.normalizeUploadUrl(res)
        if (!imageUrl) {
          throw new Error(this.$t('slideshow.upload.noUrl'))
        }
        this.formData.imageUrl = imageUrl
        this.imagePreviewUrl = imageUrl
        this.$refs.slideshowFormRef && this.$refs.slideshowFormRef.validateField('imageUrl')
        this.$message.success(this.$t('slideshow.upload.success'))
      } catch (error) {
        this.formData.imageUrl = ''
        this.$message.error(error.message || this.$t('slideshow.upload.failed'))
      } finally {
        this.uploadLoading = false
      }
    },
    normalizeUploadUrl(res) {
      if (!res) return ''
      if (typeof res === 'string') return res
      const data = res.data || res
      return data.url || data.imageUrl || data.fileUrl || data.path || data.filePath || ''
    },
    submitSlideshow() {
      this.$refs.slideshowFormRef.validate(async valid => {
        if (!valid || this.uploadLoading) return
        this.submitLoading = true
        try {
          const params = { ...this.formData }
          if (this.isEdit) {
            await updateSlideshow(params)
            this.$message.success(this.$t('slideshow.audit.updateSuccess'))
          } else {
            delete params.slideId
            await saveSlideshow(params)
            this.$message.success(this.$t('slideshow.audit.saveSuccess'))
          }
          this.dialogVisible = false
          this._queryMethod()
        } catch (error) {
          this.$message.error(error.message || this.$t('slideshow.audit.saveFailed'))
        } finally {
          this.submitLoading = false
        }
      })
    },
    resetDialogForm() {
      this.revokeLocalPreviewUrl()
      this.formData = this.getDefaultFormData()
      this.imagePreviewUrl = ''
      this.$refs.slideshowFormRef && this.$refs.slideshowFormRef.clearValidate()
    },
    handleDialogClose() {
      this.resetDialogForm()
      this.isEdit = false
    },
    revokeLocalPreviewUrl() {
      if (this.localPreviewUrl) {
        URL.revokeObjectURL(this.localPreviewUrl)
        this.localPreviewUrl = ''
      }
    },
    handleSizeChange(val) {
      this.pagination.size = val
      this.pagination.current = 1
      this._listData(this.pagination.current, val)
    },
    handleCurrentChange(val) {
      this.pagination.current = val
      this._listData(val, this.pagination.size)
    },
    headerCellStyle() {
      return { background: '#EBF2FA', color: '#364153', fontSize: '14px', fontWeight: '500', fontFamily: 'PingFang SC', height: '50px' }
    },
    cellStyle({ row }) {
      const rowIndex = this.tableData.indexOf(row)
      return rowIndex % 2 === 1
        ? { background: '#F8FBFF', color: '#1D2129', fontSize: '14px', fontFamily: 'PingFang SC', height: '50px' }
        : { background: 'white', color: '#1D2129', fontSize: '14px', fontFamily: 'PingFang SC', height: '50px' }
    }
  }
}
</script>

<style scoped>
.admin-room-container { height: calc(100vh - 60px); background: #EBF2FA; padding: 0; overflow: hidden; box-sizing: border-box; display: flex; flex-direction: column; }
.figma-white-card { background: white; border-radius: 0 0 12px 12px; flex: 1; min-height: 0; display: flex; flex-direction: column; }
.figma-page-header { display: flex; flex-direction: column; align-items: flex-start; padding: 20px 24px; gap: 12px; }
.figma-page-title { font-size: 16px; font-weight: 500; color: #1D2129; font-family: 'PingFang SC'; margin-bottom: 8px; }
.figma-header-divider { width: 100%; height: 1px; background: #DCE6F5; margin-bottom: 16px; }
.figma-header-row { display: flex; align-items: center; justify-content: space-between; width: 100%; }
.figma-btn-group { display: flex; align-items: center; gap: 8px; }
.figma-btn-add { height: 38px; border-radius: 10px !important; border: none !important; background: #0E76FF !important; color: white !important; font-size: 14px; font-weight: 500; padding: 0 16px; display: inline-flex; align-items: center; gap: 6px; }
.figma-btn-add:hover { background: #0D6FE0 !important; }
.figma-btn-refresh { height: 38px; width: 38px; border-radius: 10px !important; border: 1px solid #DCE6F5 !important; background: white !important; color: #6A7282 !important; display: inline-flex; align-items: center; justify-content: center; }
.figma-btn-refresh:hover { border-color: #0E76FF !important; color: #0E76FF !important; }
.figma-search-group { display: flex; align-items: center; gap: 8px; margin-left: auto; }
.figma-search-input { width: 256px; height: 38px; }
.figma-search-input >>> .el-input__inner { border-radius: 10px; border: none; outline: 1px #0E76FF solid; outline-offset: -1px; height: 38px; font-size: 14px; background: white; }
.figma-btn-search { height: 38px; border-radius: 10px !important; border: none !important; outline: 1px #0E76FF solid !important; outline-offset: -1px; color: #0E76FF !important; background: white !important; font-size: 14px; font-weight: 500; padding: 0 16px; display: inline-flex; align-items: center; gap: 6px; }
.figma-btn-search:hover { background: rgba(14, 118, 255, 0.08) !important; }
.figma-table-section { flex: 1; padding: 0 24px; overflow: auto; }
.figma-table-section >>> .el-table { border-radius: 8px; overflow: hidden; border: none; }
.figma-table-section >>> .el-table__header th { background: #EBF2FA !important; border: none !important; }
.figma-table-section >>> .el-table__body td { border: none !important; border-bottom: 1px solid #F0F0F0 !important; }
.figma-table-section >>> .el-table__body tr:nth-child(even) td { background: #F8FBFF; }
.figma-table-section >>> .el-table__body tr:hover > td { background: #EBF2FA !important; }
.figma-link-text { color: #0E76FF; font-size: 13px; word-break: break-all; }
.drag-handle { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 6px; color: #0E76FF; cursor: move; }
.drag-handle:hover { background: rgba(14, 118, 255, 0.10); }
.drag-handle i { font-size: 16px; }
.figma-table-section >>> .is-dragging-row td { opacity: 0.55; }
.figma-table-section >>> .is-drag-over-row td { background: #E8F3FF !important; }
.status-tag { display: inline-block; padding: 4px 12px; border-radius: 8px; font-size: 14px; font-weight: 400; }
.status-normal { background: rgba(33, 185, 86, 0.10); color: #21B956; border: 1px solid #21B956; }
.status-danger { background: rgba(251, 44, 54, 0.10); color: #FB2C36; border: 1px solid #FB2C36; }
.status-warning { background: rgba(255, 153, 0, 0.10); color: #FF9900; border: 1px solid #FF9900; }
.action-links { display: inline-flex; align-items: center; justify-content: center; width: 100%; gap: 18px; }
.action-link { color: #0E76FF; font-size: 14px; cursor: pointer; text-decoration: none; white-space: nowrap; }
.action-link:hover { text-decoration: underline; }
.action-link-danger { color: #FB2C36; }
.link-separator { color: #DCE6F5; }
.figma-pagination { display: flex; align-items: center; justify-content: flex-end; padding: 16px 24px; gap: 16px; border-top: 1px solid #DCE6F5; }
.pagination-total { font-size: 14px; color: #1D2129; }
.figma-pagination >>> .el-pagination { display: flex; align-items: center; }
.figma-pagination >>> .el-pager li { border-radius: 8px; }
.figma-pagination >>> .el-pager li.active { background: #E8F3FF; color: #0E76FF; }
.pagination-size-select { width: 92px; }
.pagination-size-select >>> .el-input__inner { border-radius: 8px; background: #F2F3F5; border: none; font-size: 14px; }
.slideshow-dialog-form >>> .el-form-item { margin-bottom: 20px; }
.slideshow-dialog-form >>> .el-form-item__label { color: #1D2129; font-size: 14px; font-weight: 500; font-family: 'PingFang SC'; line-height: 38px; padding-right: 12px; }
.slideshow-dialog-form >>> .el-form-item__content { line-height: 38px; }
.slideshow-dialog-form >>> .el-input__inner { height: 38px; border-radius: 10px; border: 1px solid #DCE6F5; color: #1D2129; font-size: 14px; }
.slideshow-dialog-form >>> .el-input__inner::placeholder { color: #8C9BB4; }
.slideshow-image-form-item { margin-bottom: 22px !important; }
.slideshow-image-form-item >>> .el-form-item__label { line-height: 38px; }
.slideshow-uploader,
.slideshow-uploader >>> .el-upload,
.slideshow-preview,
.slideshow-upload-placeholder { width: 82px; height: 82px; }
.slideshow-preview,
.slideshow-upload-placeholder { border-radius: 8px; border: 1px solid #0E76FF; overflow: hidden; display: flex; align-items: center; justify-content: center; background: rgba(14, 118, 255, 0.08); cursor: pointer; position: relative; box-sizing: border-box; }
.slideshow-preview >>> .el-image { width: 100%; height: 100%; }
.slideshow-preview-mask { position: absolute; inset: 0; background: rgba(0, 0, 0, 0.42); color: #fff; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 4px; opacity: 0; transition: opacity .2s; font-size: 12px; }
.slideshow-preview:hover .slideshow-preview-mask { opacity: 1; }
.slideshow-upload-placeholder i { font-size: 28px; color: #0E76FF; font-weight: 600; }
.slideshow-status-form-item { margin-bottom: 28px !important; }
.slideshow-status-form-item >>> .el-form-item__content { display: flex; align-items: center; gap: 12px; }
.slideshow-status-text { color: #8C9BB4; font-size: 14px; font-weight: 500; font-family: 'PingFang SC'; }
.slideshow-link-row { display: flex; align-items: center; gap: 12px; }
.slideshow-link-row >>> .el-input { flex: 1; }
.slideshow-select-link-btn,
.slideshow-btn-confirm { width: 98px; height: 38px; border-radius: 10px !important; border: none !important; background: #0E76FF !important; color: #fff !important; font-size: 14px; font-weight: 400; padding: 0; }
.slideshow-dialog-footer { display: flex; justify-content: flex-end; gap: 12px; }
.slideshow-btn-cancel { width: 98px; height: 38px; border-radius: 10px !important; border: 1px solid #0E76FF !important; background: #fff !important; color: #0E76FF !important; font-size: 14px; font-weight: 500; padding: 0; }
>>> .figma-slideshow-dialog { border-radius: 12px; overflow: hidden; }
>>> .figma-slideshow-dialog .el-dialog__header { height: 66px; padding: 0 24px; background: #EBF2FA; border-bottom: 1px solid #EDF2FA; display: flex; align-items: center; box-sizing: border-box; }
>>> .figma-slideshow-dialog .el-dialog__title { color: #1D2129; font-size: 16px; font-weight: 600; font-family: 'PingFang SC'; }
>>> .figma-slideshow-dialog .el-dialog__body { padding: 24px 24px 0; }
>>> .figma-slideshow-dialog .el-dialog__footer { padding: 0 24px 28px; border-top: none; }
</style>
